import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { addUser, updateUser, fetchUserById } from "../services/api";
import AdminLayout from "../components/layout/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const ROLE_OPTIONS = [
  { value: "CUSTOMER", label: "Customer" },
  { value: "ADMIN", label: "Admin" },
  { value: "SUPER_ADMIN", label: "Super Admin" },
  { value: "AGENT", label: "Agent" },
  { value: "SUPPORT", label: "Support" },
];

const SuperAdminUserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isEdit = !!id;

  const {
    data: response,
    isLoading: isFetching,
    error: fetchError
  } = useQuery({
    queryKey: ["user", id],
    queryFn: () => fetchUserById(id),
    enabled: isEdit,
    staleTime: 5 * 60 * 1000,
  });

  const user = response?.user;

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    patientId: "",
    status: "active",
    role: "CUSTOMER",
    password: "",
  });

  useEffect(() => {
    if (isEdit && user) {
      const fullName = user.full_name || user.name || "";
      const [firstName, ...rest] = fullName.split(" ");
      setFormData((prev) => ({
        ...prev,
        first_name: user.first_name || firstName || "",
        last_name: user.last_name || rest.join(" ") || "",
        email: user.email || "",
        phone: user.phone || user.phone_number || "",
        patientId: user.hospital_number || user.patientId || "",
        status: user.status || "active",
        role: user.role || "CUSTOMER",
      }));
    }
  }, [user, isEdit]);

  const addMutation = useMutation({
    mutationFn: (data) => addUser({
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      phone_number: data.phone,
      password: data.password,
      role: data.role,
      hospital_number: data.patientId || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(["hospitalUsers"]);
      navigate("/superadmin/users");
    },
    onError: (err) => {
      alert("Failed to create user: " + (err?.response?.data?.message || err.message || "Unknown error"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["hospitalUsers"]);
      queryClient.invalidateQueries(["user", id]);
      navigate("/superadmin/users");
    },
    onError: (err) => {
      alert("Failed to update user: " + (err?.response?.data?.message || err.message || "Unknown error"));
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim()) {
      alert("First name, last name and email are required fields.");
      return;
    }

    if (!isEdit && (!formData.password || formData.password.length < 6)) {
      alert("Password is required and must be at least 6 characters.");
      return;
    }

    if (isEdit) {
      updateMutation.mutate({ id, data: { first_name: formData.first_name, last_name: formData.last_name } });
    } else {
      addMutation.mutate(formData);
    }
  };

  if (isEdit && isFetching) {
    return (
      <AdminLayout>
        <div className="max-w-3xl mx-auto py-12 px-4 text-center text-gray-600">
          Loading user information...
        </div>
      </AdminLayout>
    );
  }

  if (isEdit && fetchError) {
    return (
      <AdminLayout>
        <div className="max-w-3xl mx-auto py-12 px-4 text-center">
          <p className="text-red-600 mb-4">Failed to load user data</p>
          <p className="text-gray-600 mb-6">{fetchError.message || "Please try again later."}</p>
          <Button onClick={() => navigate("/superadmin/users")}>
            Back to Users
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const saving = addMutation.isLoading || updateMutation.isLoading;

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold mb-6">
          {isEdit ? "Edit User" : "Add New User"}
        </h1>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow-sm p-6 space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                placeholder="First name"
                disabled={saving}
              />
            </div>
            <div>
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                placeholder="Last name"
                disabled={saving}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="user@example.com"
              disabled={saving || isEdit}
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="08012345678"
              disabled={saving || isEdit}
            />
          </div>

          <div>
            <Label htmlFor="patientId">Hospital Number / Patient ID (optional)</Label>
            <Input
              id="patientId"
              name="patientId"
              value={formData.patientId}
              onChange={handleChange}
              placeholder="e.g. 4567890344"
              disabled={saving || isEdit}
            />
          </div>

          <div>
            <Label htmlFor="role">Role</Label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              disabled={saving || isEdit}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            {isEdit && (
              <p className="text-xs text-gray-400 mt-1">Role cannot be changed after creation.</p>
            )}
          </div>

          {!isEdit && (
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Minimum 6 characters"
                disabled={saving}
              />
            </div>
          )}

          {isEdit && (
            <div>
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                disabled={saving}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}

          <div className="flex justify-end gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/superadmin/users")}
              disabled={saving}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={saving}
            >
              {saving
                ? "Saving..."
                : isEdit
                ? "Update User"
                : "Create User"}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default SuperAdminUserForm;
